/**
 * Shared utilities for creating flexible layout sections
 *
 * Extracted from V2FlexibleSectionsSection so both the regular sections editor
 * and the sticky container editor can reuse section creation logic.
 */

import type { LayoutSection, SectionKind } from '@segmently/schema-models/v2';
import { createDefaultFlexiblePurchaseButton } from './v2FlexiblePurchaseButtonConfig';
import { createDefaultV2ProductCatalog } from './v2PaywallSubscriptionsConfig';
import { DEFAULT_EMBED_STRUCTURE } from './v2EmbedConfig';
import { createDefaultV2CloseButton, createDefaultV2HeaderAppearance } from './v2PaywallHeaderConfig';
import type { OnboardingThemeSettingsV2 } from '@modules/firebase-core/converters/onboardingConverter';
import { getThemedFlexibleSectionContent } from '../utils/v2ThemeDefaults';

/**
 * Extended section kinds that exist at runtime but are not yet in the schema SectionKind type.
 * Includes PurchaseButton as a virtual UI-only kind.
 */
export type AddMenuKind = SectionKind
  | 'PurchaseButton'
  | 'SingleSelectionList'
  | 'MultipleSelectionList'
  | 'BulletList'
  | 'PaywallHeader';

const SECTION_KIND_LABELS: Record<string, string> = {
  Background: 'Background',
  Header: 'Header bar',
  Footer: 'Footer / Action bar',
  PaywallHeader: 'Paywall Header',
  Text: 'Text',
  Media: 'Media (image / video)',
  ProductCatalog: 'Product Catalog',
  Button: 'Button',
  OptionsList: 'Options List',
  SingleSelectionList: 'Single Selection',
  MultipleSelectionList: 'Multiple Selection',
  BulletList: 'Bullet List',
  CustomEmbed: 'Custom Embed (HTML)',
  TextField: 'Text Field',
  EmptySpace: 'Empty Space',
  ArticleIndex: 'Article index',
};

/** Default ArticleIndex heading (C-23). English text for every language: only the KEY follows the article (R-32). */
export const ARTICLE_INDEX_DEFAULT_HEADING = 'More articles';

/** Context a palette can pass when adding a section; only article documents set it. */
export interface CreateDefaultSectionOptions {
  /** The article's `defaultLanguage` — the language key the ArticleIndex validator requires on the heading (R-32). */
  articleDefaultLanguage?: string;
}

export function generateSectionId(): string {
  return `section-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 7)}`;
}

/** Create fallback sample list items when no theme provides items */
function createFallbackListItems(locale: string, count = 3) {
  return Array.from({ length: count }, (_, i) => ({
    title: { translations: { [locale]: `Option ${i + 1}` }, appearance: {} },
    action: { kind: 'next', edges: [] },
  }));
}

/**
 * Create a default LayoutSection for the given kind.
 * @param menuKind - SectionKind or 'PurchaseButton' (virtual UI kind)
 * @param order - render order
 * @param locale - needed for PurchaseButton default translations
 * @param themeSettingsV2 - optional theme for styled defaults
 * @param options - article context (ArticleIndex heading language)
 */
export function createDefaultSection(
  menuKind: AddMenuKind,
  order: number,
  locale?: string,
  themeSettingsV2?: OnboardingThemeSettingsV2 | null,
  options?: CreateDefaultSectionOptions,
): LayoutSection {
  // PurchaseButton is a virtual kind — actual schema kind is 'Button' with action.kind='purchase'
  const kind = (menuKind === 'PurchaseButton' ? 'Button' : menuKind) as string;

  // Try to extract themed defaults from the theme's FlowDocument
  const themedContent = themeSettingsV2
    ? getThemedFlexibleSectionContent(themeSettingsV2, menuKind, locale || 'en')
    : null;

  return {
    id: generateSectionId(),
    order,
    kind,
    label: menuKind === 'PurchaseButton' ? 'Purchase Button' : (SECTION_KIND_LABELS[kind] || kind),
    layout: {
      heightKind: 'auto',
      flexGrow: kind === 'Media' ? 1 : 0,
      flexShrink: 1,
      scrollable: false,
    },
    ...(kind === 'Text' && {
      textContent: themedContent?.textContent ?? {
        title: { kind: 'static', translations: locale ? { [locale]: 'Heading' } : {}, appearance: {} },
      },
    }),
    ...(kind === 'Media' && {
      mediaContent: themedContent?.mediaContent ?? {
        kind: 'Image',
        content: {
          image: {
            translations: {
              [locale || 'en']: {
                original: 'https://dev-api.segmently.ai/assets/onboardings/global-themes/a23df18f-281d-4d01-ae67-3822a6bc7e29/images/origin/en-US_1769515244797_1738462175991-aacva33.png',
              },
            },
          },
        },
        appearance: {},
      },
    }),
    ...(kind === 'Button' && menuKind === 'PurchaseButton' && {
      buttonContent: themedContent?.buttonContent ?? createDefaultFlexiblePurchaseButton(locale || 'en'),
    }),
    ...(kind === 'Button' && menuKind !== 'PurchaseButton' && {
      buttonContent: themedContent?.buttonContent ?? {
        kind: 'label',
        content: {
          translations: locale ? { [locale]: 'Button' } : {},
          kind: 'static',
          appearance: {},
        },
        appearance: { fullWidth: true, dimensions: { height: 50 }, borders: { radius: { uniform: 12 } } },
        action: { kind: 'next', edges: [] },
      },
    }),
    ...(kind === 'OptionsList' && {
      optionsListContent: {
        selectionMode: 'one',
        itemLayout: 'Title',
        items: createFallbackListItems(locale || 'en'),
        ...(themedContent?.optionsListContent as Record<string, unknown> | undefined),
      },
    }),
    ...(kind === 'SingleSelectionList' && {
      optionsListContent: {
        selectionMode: 'one',
        itemLayout: 'Title',
        items: createFallbackListItems(locale || 'en'),
        ...(themedContent?.optionsListContent as Record<string, unknown> | undefined),
      },
    }),
    ...(kind === 'MultipleSelectionList' && {
      optionsListContent: {
        selectionMode: 'many',
        itemLayout: 'TitleCheckbox',
        items: createFallbackListItems(locale || 'en'),
        ...(themedContent?.optionsListContent as Record<string, unknown> | undefined),
      },
    }),
    ...(kind === 'BulletList' && {
      optionsListContent: {
        selectionMode: 'browse',
        itemLayout: 'ImageTitle',
        items: createFallbackListItems(locale || 'en'),
        ...(themedContent?.optionsListContent as Record<string, unknown> | undefined),
      },
    }),
    ...(kind === 'PaywallHeader' && {
      paywallHeaderContent: themedContent?.paywallHeaderContent ?? {
        closeButton: createDefaultV2CloseButton(),
        headerAppearance: createDefaultV2HeaderAppearance(),
      },
    }),
    ...(kind === 'CustomEmbed' && {
      embedContent: { ...DEFAULT_EMBED_STRUCTURE },
    }),
    ...(kind === 'ProductCatalog' && {
      productCatalogContent: themedContent?.productCatalogContent ?? createDefaultV2ProductCatalog(),
    }),
    ...(kind === 'TextField' && {
      textFieldContent: themedContent?.textFieldContent
        ? { fieldType: 'string', value: '', appearance: {}, ...(themedContent.textFieldContent as Record<string, unknown>) }
        : {
            fieldType: 'string',
            placeholder: { kind: 'static', translations: {}, appearance: {} },
            value: '',
            appearance: {},
          },
    }),
    // D-7 / D-8: cover, date and description on, topic off; limit 12, last updated; no filter = all live articles.
    ...(kind === 'ArticleIndex' && {
      articleIndexContent: {
        heading: { [options?.articleDefaultLanguage || locale || 'en']: ARTICLE_INDEX_DEFAULT_HEADING },
        sort: 'dateModified',
        limit: 12,
        showThumbnail: true,
        showTopic: false,
        showDate: true,
        showDescription: true,
      },
    }),
  } as unknown as LayoutSection;
}
